// App.tsx
import React, { useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import LoginPage from './features/auth/pages/LoginPage';
import MainScreen from './features/mainScreen/pages/MainScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { InstitutionProvider, useInstitution } from './contexts/InstitutionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Theme Component
const AppTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { institution } = useInstitution();
  
  const theme = useMemo(() => createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: institution.primaryColor
      },
      secondary: {
        main: institution.secondaryColor || '#6e6e6e'
      }
    },
  }), [institution]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

interface LocationState {
  from: Location;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { kullaniciModal } = useAuth();
  const location = useLocation();
  if (!kullaniciModal) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <InstitutionProvider>
        <AuthProvider>
          <AppTheme>
            <Router>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/mainScreen"
                  element={
                    <ProtectedRoute>
                      <MainScreen />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/mainScreen" replace />} />
                <Route path="*" element={<Navigate to="/mainScreen" replace />} />
              </Routes>
            </Router>
          </AppTheme>
        </AuthProvider>
      </InstitutionProvider>
    </QueryClientProvider>
  );
};

export default App;